/**
 * Phone number utilities for The Uncanny House.
 * Display format:  1 (555) 555-5555
 * Storage format:  +15555555555  (E.164)
 */

/** Strip everything but digits */
const onlyDigits = (s) => (s || "").replace(/\D/g, "");

/**
 * Format a partial input as the user types.
 * Always prepends "1" if missing.
 * Examples:
 *   "555"                 -> "1 (555"
 *   "5551234"             -> "1 (555) 123-4"
 *   "5551234567"          -> "1 (555) 123-4567"
 *   "15551234567"         -> "1 (555) 123-4567"
 *   "+1 (555) 123-4567"   -> "1 (555) 123-4567"
 */
export function formatPhone(input) {
  let digits = onlyDigits(input);
  if (digits.length === 0) return "";
  // Ensure we have a country-code "1" up front.
  if (digits[0] !== "1") digits = "1" + digits;
  // Cap to 11 (1 + 10).
  digits = digits.slice(0, 11);

  const c = digits[0]; // always "1"
  const area = digits.slice(1, 4);
  const mid = digits.slice(4, 7);
  const last = digits.slice(7, 11);

  if (digits.length === 1) return c;
  if (digits.length <= 4) return `${c} (${area}`;
  if (digits.length <= 7) return `${c} (${area}) ${mid}`;
  return `${c} (${area}) ${mid}-${last}`;
}

/**
 * Normalize a formatted phone to E.164 (+1XXXXXXXXXX) for storage / API submission.
 * Returns "" if the input is empty.
 */
export function normalizePhone(input) {
  let digits = onlyDigits(input);
  if (!digits) return "";
  if (digits[0] !== "1") digits = "1" + digits;
  digits = digits.slice(0, 11);
  return `+${digits}`;
}

/** A US/CA phone must be 11 digits including the leading 1 (i.e. 10 digits of NANP). */
export function isValidPhone(input) {
  const digits = onlyDigits(input);
  const normalized = digits[0] === "1" ? digits : "1" + digits;
  return normalized.length === 11;
}

/** Standard error message returned to forms. */
export const PHONE_ERROR =
  "Please enter a complete 10-digit phone number, e.g. 1 (555) 555-5555.";
